import { makeStyles, Theme, createStyles, Button, Typography, Dialog, AppBar, Toolbar, IconButton } from '@material-ui/core';
import { grey } from '@material-ui/core/colors';
import React, { useRef, useState, useContext, useEffect, useMemo, FC } from 'react';
import { catchError } from 'rxjs/operators';
import { useDataFieldValidity, registry, FormContext, useDataValue, FormControlProps } from '..';
import { useDispatch } from 'react-redux';
import { SignatureCaptureControlDefinition } from '../services/layout';
import { delayedUploadImageActions } from '../../../../redux';
import { Create, Close, Save } from '@material-ui/icons';
import SignaturePad from 'signature_pad';
import { EMPTY } from 'rxjs';
import { FormControl } from './FormControl';
// const uuidv4 = require ('uuidv4');
import { v4 as uuidv4 } from 'uuid';
export interface SignatureCaptureProps extends FormControlProps<SignatureCaptureControlDefinition> {
  // add constructor signature like the other
}

const useStyles = makeStyles(function (theme: Theme) {
  return createStyles({
    root: {
      display: 'flex',
      flex: 1,
      alignItems: 'stretch',
      touchAction: 'none',
      //TODO: resolve sizing issue in a more elegant way
      maxWidth: '100%',
      maxHeight: '100%'
    },
    toolbar: {
      justifyContent: 'space-between'
    },
    label: {
      marginLeft: -theme.spacing(1),
      flex: 1
    },
    canvas: {
      flex: 1,
      zIndex: 1,
      //TODO: resolve sizing issue in a more elegant way
      maxWidth: '100%',
      maxHeight: '100%'
    },
    thumbnail: {
      padding: 0
    },
    signButton: {
      textTransform: 'none',
      position: 'absolute',
      top: 0,
      left: 0,
      margin: '2px'
    },
    signText: {
      bottom: theme.spacing(1),
      position: 'relative'
    },
    toolbarButton: {
      textTransform: 'none',
      marginLeft: '10px',
      marginRight: '10px'
    },
    signatureImage: {
      width: '176px',
      height: '99px',
      border: 'solid 1px',
      objectFit: 'contain'
    },
    hr: {
      display: 'flex',
      position: 'absolute',
      top: '60%',
      left: '10%',
      right: '10%'
    },
    ballot: {
      color: grey[600],
      position: 'relative',
      left: '-2px',
      bottom: '-15px'
    },
    divider: {
      width: '100%',
      position: 'absolute',
      borderBottom: 'solid',
      color: grey[300],
      bottom: 0
    },
    placeholder: {
      position: 'absolute',
      display: 'flex',
      alignSelf: 'center',
      verticalAlign: 'middle',
      textTransform: 'none',
      color: theme.palette.secondary.main
    },
    cloud: {
      alignSelf: 'center',
      margin: theme.spacing(1)
    }
  });
});

export const SignatureCapture: FC<SignatureCaptureProps> = function (props) {
  const definition = props.definition;
  const canvasRef = useRef<HTMLCanvasElement>();
  const signaturePadRef = useRef<SignaturePad | undefined>();
  const rootRef = useRef();
  const [ open, setOpen ] = useState(false);
  const [ ready, setReady ] = useState(false);
  const { scope, evalID, imageService } = useContext(FormContext);
  const imageId = useDataValue(definition.dataPath) as string;
  const [ image, setImage ] = useState<string | null>(null);
  const [ placeholder, setPlaceholder ] = useState(false);
  const [empty, setEmpty] = useState(true);

  useEffect(function loadImage() {
    if (imageId) {
      const sub_1 = imageService!
        .getImageData([imageId])
        .subscribe(function (_a) {
        const [ image ] = _a;

        if (image?.uri) {
          const p = new Promise(function (resolve, reject) {
            setImage(image.uri);
            setPlaceholder(false);
          });
          Promise.resolve(p);
        }
        else {
          imageService!
            .downloadImage(imageId)
            .pipe(
              catchError(function (error) {
                console.log(error);
                return EMPTY;
              })
            )
            .subscribe(function (newImageData) {
            setImage(newImageData.uri);
            setPlaceholder(false);
          });
        }
      });
      return function () { return sub_1.unsubscribe(); };
    }
    else {
      setImage(null);
      setPlaceholder(false);
    }
  }, [imageId]);
  useEffect(function () {
    setReady(open);
  }, [open]);
  useEffect(function createSignaturePad() {
    if (!ready)
      return;

    signaturePadRef.current! = new SignaturePad(canvasRef!.current!, {
      onEnd: function() {
        setEmpty(false);
      }
    });
    signaturePadRef.current!.minWidth = 2;
    signaturePadRef.current!.maxWidth = 6;

    return () => {
      signaturePadRef.current!.off();
    }
  }, [ready]);
  const classes = useStyles(props);
  const error = useDataFieldValidity(definition.dataPath);

  const dispatch = useDispatch();
  const canvas = useMemo(function () {
    return open ? (React.createElement('canvas', { className: classes.canvas, ref: canvasRef })) : null;
  }, [open]);

  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (canvasRef.current) {
      setHeight(canvasRef!.current!.clientHeight);
      setWidth(canvasRef!.current!.clientWidth);
    }
  }, [ready])

  //console.log('root size', rootsize)
  useEffect(function setCanvasSize() {
    if (!ready)
      return;
    // do need to account for size here!
    //console.log(size, devicePixelRatio, canvasRef.current.width, canvasRef.current.height, canvasRef.current.offsetWidth, canvasRef.current.offsetHeight)
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    if (canvasRef.current) {

      canvasRef!.current!.width = width * ratio;
      canvasRef!.current!.height = height * ratio;
      canvasRef!.current!.getContext('2d')!.scale(ratio, ratio);
    }
    if (signaturePadRef.current) {
      const signatureTemp = signaturePadRef!.current!.toData();
      signaturePadRef!.current!.clear();
      signaturePadRef!.current!.fromData(signatureTemp);
    }
  }, [ready, width]);
  // Button Event actions
  const clearSignature = function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    signaturePadRef!.current!.clear();
    setEmpty(true);
  };
  const cancelSignature = function (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    setOpen(false);
  };
  const saveSignature = function (event: any) {
      dispatch(delayedUploadImageActions.request({
        id: uuidv4(),
        uri: signaturePadRef!.current!.toDataURL(),
        scope: scope,
        evalID: evalID as string,
        path: definition.dataPath
      }));
      setOpen(false);
  };
  // typings
  return (
    React.createElement(FormControl, { label: definition.label, helpText: definition.helpText, footnote: definition.footnote, error: error },
    React.createElement('div', null,
      React.createElement(Button, { onClick: function () { return setOpen(true); }, className: classes.thumbnail },
        React.createElement('div', { className: classes.signButton },
          React.createElement(Create, { fontSize: 'small' }),
          React.createElement(Typography, { className: classes.signText, variant: 'caption' }, 'Click to Sign')),
        React.createElement('img', { src: image, className: classes.signatureImage }),
        placeholder && (
        // <div className={classes.placeholder}>
        React.createElement(Typography, { variant: 'body1', className: classes.placeholder }, 'Signature in cloud')
        /* <CloudIcon className={classes.cloud} /> */
        /* </div> */
        )),
      React.createElement(Dialog, { 'aria-labelledby': 'signature-capture', fullScreen: true,
        //onClose={cancelSignature}
        open: open, onKeyDown: function (event) {
          if (event.key === 'Enter') {
            {
              // ?? this was an unused expression
              saveSignature(event);
            }
          }
        } },
        React.createElement('div', { className: classes.root, ref: rootRef },
          React.createElement(AppBar, null,
            React.createElement(Toolbar, { className: classes.toolbar },
              React.createElement('div', null,
                React.createElement(IconButton, { 'aria-label': 'cancel', onClick: cancelSignature, size: 'medium', color: 'inherit', className: classes.toolbarButton },
                  React.createElement(Close, null)),
                React.createElement(Button, { 'aria-label': 'clear', onClick: clearSignature, size: 'large', className: classes.toolbarButton, variant: 'contained' }, 'Clear')),
              React.createElement('div', null,
                React.createElement(Button, { 'aria-label': 'save', disabled: empty, onClick: saveSignature, size: 'large', className: '' + classes.toolbarButton, variant: 'contained', color: 'secondary', startIcon: React.createElement(Save, null) }, 'Save')))),
          canvas,
          React.createElement('div', { className: classes.hr },
            React.createElement('div', { className: classes.divider }),
            React.createElement(Typography, { variant: 'h3', className: classes.ballot }, '\u2717')))))));
};